import React, { useState, useEffect } from 'react';
import { PlusCircle, X, Loader2, AlertCircle, Edit, Trash2, Eye, Send, Copy } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const apiService = {
  get: (url, config = {}) => {
    const token = localStorage.getItem('access_token');
    return axios.get(`${API_BASE_URL}${url}`, {
      ...config,
      headers: {
        Authorization: `Bearer ${token}`,
        ...config.headers
      }
    });
  },
  post: (url, data, config = {}) => {
    const token = localStorage.getItem('access_token');
    return axios.post(`${API_BASE_URL}${url}`, data, {
      ...config,
      headers: {
        Authorization: `Bearer ${token}`,
        ...config.headers
      }
    });
  },
  put: (url, data, config = {}) => {
    const token = localStorage.getItem('access_token');
    return axios.put(`${API_BASE_URL}${url}`, data, {
      ...config,
      headers: {
        Authorization: `Bearer ${token}`,
        ...config.headers
      }
    });
  },
  delete: (url, config = {}) => {
    const token = localStorage.getItem('access_token');
    return axios.delete(`${API_BASE_URL}${url}`, {
      ...config,
      headers: {
        Authorization: `Bearer ${token}`,
        ...config.headers
      }
    });
  }
};

// Component for displaying templates by role
const TemplateRoleSection = ({ title, roleKey, templates, onEdit, onPreview, emptyMessage, bgColor, borderColor, textColor }) => {
  if (templates.length === 0) {
    return (
      <div className={`${bgColor} ${borderColor} border rounded-xl p-4`}>
        <h2 className={`text-base font-bold ${textColor} mb-2`}>{title}</h2>
        <div className="text-center py-4">
          <div className="text-slate-500 text-xs">{emptyMessage}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${bgColor} ${borderColor} border rounded-xl`}>
      <div className="px-4 py-2 border-b border-steel-200 flex items-center justify-between">
        <h2 className={`text-base font-bold ${textColor}`}>{title}</h2>
        <div className="text-xs text-slate-600">
          {templates.length} template{templates.length !== 1 ? 's' : ''}
        </div>
      </div>

      <div className="p-3">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
          {templates.map((template) => (
            <div key={template.id} className="bg-white border border-steel-200 rounded-lg p-3 hover:shadow-md hover:bg-brand-50 transition-all">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-slate-900 text-sm truncate">{template.name}</h3>
                  <div className="text-xs text-slate-500 truncate">{template.subject}</div>
                </div>
                <div className="flex space-x-1 ml-2">
                  <button
                    onClick={() => onPreview(template)}
                    className="text-brand-500 hover:text-brand-700 p-1"
                    title="Preview"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => onEdit(template)}
                    className="text-brand-500 hover:text-brand-700 p-1"
                    title="Edit"
                  >
                    <Edit className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                  template.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-steel-100 text-slate-800'
                }`}>
                  {template.is_active ? 'Active' : 'Inactive'}
                </span>
                <div className="text-xs text-slate-400">
                  {new Date(template.created_at).toLocaleDateString('en-US')}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const EmailTemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [templateTypes, setTemplateTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [currentTab, setCurrentTab] = useState('html');
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);

  // Group templates by role
  const groupTemplatesByRole = (templates) => {
    return {
      participants: templates.filter(t => t.recipient_role === 'participants'),
      supervisors: templates.filter(t => t.recipient_role === 'supervisors'),
      training_center: templates.filter(t => t.recipient_role === 'training_center')
    };
  };

  const [formData, setFormData] = useState({
    name: '',
    template_type: '',
    subject: '',
    html_content: '',
    text_content: '',
    is_active: true
  });

  const availableVariables = [
    '{{user_name}}',
    '{{training_name}}',
    '{{start_date}}',
    '{{end_date}}',
    '{{training_center}}',
    '{{required_items}}',
    '{{employee_list}}',
    '{{participant_list}}',
    '{{participant_count}}'
  ];

  const predefinedTemplates = {
    training_invitation: {
      name: 'Training Invitation',
      subject: 'Training Invitation: {{training_name}}',
      html_content: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <h2 style="color: #2563eb;">Training Invitation</h2>
          <p>Dear {{user_name}},</p>
          <p>We invite you to participate in the following training:</p>
          <div style="background-color: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #1f2937; margin-top: 0;">Training Details</h3>
            <p><strong>Name:</strong> {{training_name}}</p>
            <p><strong>Start Date:</strong> {{start_date}}</p>
            <p><strong>End Date:</strong> {{end_date}}</p>
            <p><strong>Location:</strong> {{training_center}}</p>
          </div>
          <div style="background-color: #fff3cd; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #856404; margin-top: 0;">Required Items</h3>
            <p>{{required_items}}</p>
          </div>
          <p>Best regards,<br>Ventro Energy Team</p>
        </div>
      `,
      text_content: `Training Invitation\n\nDear {{user_name}},\n\nWe invite you to participate in: {{training_name}}\nDate: {{start_date}} - {{end_date}}\nLocation: {{training_center}}\n\nRequired items: {{required_items}}\n\nBest regards,\nVentro Energy Team`
    },
    training_reminder: {
      name: 'Training Reminder',
      subject: 'Reminder: {{training_name}} - {{start_date}}',
      html_content: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <h2 style="color: #f59e0b;">⏰ Training Reminder</h2>
          <p>Dear {{user_name}},</p>
          <p>This is a reminder about your upcoming training:</p>
          <div style="background-color: #fef3c7; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #f59e0b;">
            <h3 style="color: #92400e; margin-top: 0;">{{training_name}}</h3>
            <p><strong>Date:</strong> {{start_date}} - {{end_date}}</p>
            <p><strong>Location:</strong> {{training_center}}</p>
          </div>
          <div style="background-color: #fee2e2; padding: 15px; border-radius: 8px;">
            <p style="color: #dc2626; font-weight: bold; margin: 0;">⚠️ Remember to bring: {{required_items}}</p>
          </div>
          <p>Best regards,<br>Ventro Energy Team</p>
        </div>
      `
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchTemplateTypes();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await apiService.get('/email-templates/');
      setTemplates(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError('Error loading templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateTypes = async () => {
    try {
      const response = await apiService.get('/email-templates/template_types/');
      setTemplateTypes(response.data);
    } catch (error) {
      console.error('Error fetching template types:', error);
    }
  };

  const handleSave = async () => {
    try {
      if (selectedTemplate) {
        await apiService.put(`/email-templates/${selectedTemplate.id}/`, formData);
        fetchTemplates();
        handleCloseModal();
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setError('Error saving template');
    }
  };


  const handlePreview = async (template) => {
    try {
      const response = await apiService.post(`/email-templates/${template.id}/preview/`);
      setPreviewData(response.data);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Error previewing template:', error);
      setError('Error previewing template');
    }
  };

  const handleOpenModal = (template) => {
    // Only allow editing existing templates
    if (template) {
      setSelectedTemplate(template);
      setFormData({
        name: template.name,
        template_type: template.template_type,
        subject: template.subject,
        html_content: template.html_content,
        text_content: template.text_content || '',
        is_active: template.is_active
      });
      setShowModal(true);
      setCurrentTab('html');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTemplate(null);
    setError(null);
  };

  const handleUsePredefined = (templateType) => {
    const predefined = predefinedTemplates[templateType];
    if (predefined) {
      setFormData(prev => ({
        ...prev,
        name: predefined.name,
        subject: predefined.subject,
        html_content: predefined.html_content,
        text_content: predefined.text_content || ''
      }));
    }
  };

  const handleCreateDefaultTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiService.post('/email-templates/create_defaults/');
      setError(null);
      alert('Default templates created successfully!');
      fetchTemplates();
    } catch (error) {
      console.error('Error creating default templates:', error);
      setError('Error creating default templates: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const insertVariable = (variable) => {
    const field = currentTab === 'html' ? 'html_content' : 'text_content';
    const textarea = document.getElementById(field);
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentValue = formData[field];
      const newValue = currentValue.substring(0, start) + variable + currentValue.substring(end);
      
      setFormData(prev => ({
        ...prev,
        [field]: newValue
      }));
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
        <span className="ml-2 text-slate-600">Loading templates...</span>
      </div>
    );
  }

  const groupedTemplates = groupTemplatesByRole(templates);

  return (
    <div className="h-full flex flex-col bg-white rounded-xl shadow-lg overflow-hidden border border-steel-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-50 to-steel-50 px-4 py-3 flex justify-between items-center border-b border-steel-200">
        <h1 className="text-xl font-bold text-slate-900">Email Templates</h1>
        <div className="text-xs text-slate-600">
          System templates - editing only
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-xl p-3 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-500">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Role-based Templates Sections */}
      <div className="space-y-4 flex-1 p-4 overflow-auto">
        {/* Participants Templates */}
        <TemplateRoleSection
          title="Training Participants"
          roleKey="participants"
          templates={groupedTemplates.participants}
          onEdit={handleOpenModal}
          onPreview={handlePreview}
          emptyMessage="No templates for participants"
          bgColor="bg-brand-50"
          borderColor="border-brand-200"
          textColor="text-brand-700"
        />

        {/* Supervisors Templates */}
        <TemplateRoleSection
          title="Supervisors"
          roleKey="supervisors"
          templates={groupedTemplates.supervisors}
          onEdit={handleOpenModal}
          onPreview={handlePreview}
          emptyMessage="No templates for supervisors"
          bgColor="bg-steel-50"
          borderColor="border-steel-300"
          textColor="text-slate-800"
        />

        {/* Training Center Templates */}
        <TemplateRoleSection
          title="Training Center"
          roleKey="training_center"
          templates={groupedTemplates.training_center}
          onEdit={handleOpenModal}
          onPreview={handlePreview}
          emptyMessage="No templates for training center"
          bgColor="bg-green-50"
          borderColor="border-green-200"
          textColor="text-green-800"
        />
        
        {/* No templates message */}
        {templates.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-steel-200">
            <div className="text-slate-500 text-lg mb-2">No email templates found</div>
            <div className="text-slate-400 text-sm mb-4">
              Create default templates to get started
            </div>
            <button
              onClick={handleCreateDefaultTemplates}
              className="bg-green-600 text-white px-4 py-2 rounded-xl hover:bg-green-700 transition-colors"
            >
              Create Default Templates
            </button>
          </div>
        )}
      </div>

      {/* Template edit/add modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden rounded-2xl">
            <div className="px-6 py-4 border-b border-steel-200">
              <h3 className="text-lg font-medium text-slate-900">
                Edit Template
              </h3>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
              <div className="space-y-4">
                {/* Basic information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Template Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      disabled
                      className="w-full border border-steel-300 rounded-xl px-3 py-2 bg-steel-100 text-slate-600"
                      placeholder="Template name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Template Type
                    </label>
                    <input
                      type="text"
                      value={selectedTemplate?.template_type_display || formData.template_type}
                      disabled
                      className="w-full border border-steel-300 rounded-xl px-3 py-2 bg-steel-100 text-slate-600"
                      placeholder="Template type"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email Subject
                  </label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full border border-steel-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    placeholder="Email subject"
                  />
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-slate-700">Active</span>
                  </label>
                </div>

                {/* Available variables */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Available variables (click to insert):
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {availableVariables.map(variable => (
                      <button
                        key={variable}
                        onClick={() => insertVariable(variable)}
                        className="px-2 py-1 bg-steel-100 border border-steel-300 rounded text-xs hover:bg-steel-200 transition-colors"
                      >
                        {variable}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-steel-200">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      onClick={() => setCurrentTab('html')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        currentTab === 'html'
                          ? 'border-brand-500 text-brand-500'
                          : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-steel-300'
                      }`}
                    >
                      HTML Content
                    </button>
                    <button
                      onClick={() => setCurrentTab('text')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        currentTab === 'text'
                          ? 'border-brand-500 text-brand-500'
                          : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-steel-300'
                      }`}
                    >
                      Text Content
                    </button>
                  </nav>
                </div>

                {/* Content editors */}
                <div>
                  {currentTab === 'html' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        HTML Content
                      </label>
                      <textarea
                        id="html_content"
                        value={formData.html_content}
                        onChange={(e) => setFormData(prev => ({ ...prev, html_content: e.target.value }))}
                        rows={15}
                        className="w-full border border-steel-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 font-mono text-sm"
                        placeholder="HTML template content..."
                      />
                    </div>
                  )}
                  {currentTab === 'text' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Text Content
                      </label>
                      <textarea
                        id="text_content"
                        value={formData.text_content}
                        onChange={(e) => setFormData(prev => ({ ...prev, text_content: e.target.value }))}
                        rows={15}
                        className="w-full border border-steel-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
                        placeholder="Text template content..."
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-steel-200 flex justify-end space-x-3">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 border border-steel-300 rounded-xl text-slate-700 hover:bg-steel-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-colors shadow-lg shadow-brand-500/20"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {showPreviewModal && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden rounded-2xl">
            <div className="px-6 py-4 border-b border-steel-200">
              <h3 className="text-lg font-medium text-slate-900">Template Preview</h3>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
              <div className="mb-4">
                <strong>Subject:</strong> {previewData.subject}
              </div>
              <div className="border border-steel-300 rounded-xl p-4">
                <div dangerouslySetInnerHTML={{ __html: previewData.html_content }} />
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-steel-200 flex justify-end">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 border border-steel-300 rounded-xl text-slate-700 hover:bg-steel-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplateManager;